/**
 * The React shell over `resolveBindings` — the only place in the app that
 * registers a hotkey.
 *
 * One provider, one registration pass. Components never call `useHotkey`
 * themselves; they call `useCommandHandler('viewer.resetCamera', fn)` and this
 * provider decides whether the chord for that command is live, based on the
 * scope stack and the resolved keymap. That indirection is what buys the
 * customization: the component that resets the camera has no idea which key
 * does it, so the user can change the key without touching the component.
 *
 * Everything interesting is in `resolve.ts`. This file is state plumbing.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  HotkeysProvider,
  useHotkeys,
  useHotkeySequences,
  type HotkeysProviderOptions,
  type UseHotkeyDefinition,
  type UseHotkeySequenceDefinition,
} from '@tanstack/react-hotkeys';
import { logger } from '@/lib/logger';
import { commandsById } from './catalog';
import { resolveKeymap } from './keymap';
import { loadKeymapOverrides, saveKeymapOverrides } from './keymapStorage';
import { resolveBindings, type ResolvedBinding } from './resolve';
import {
  isSequenceBinding,
  type Binding,
  type CommandId,
  type CommandScope,
  type Keymap,
  type KeymapOverrides,
} from './types';

/** A command's behaviour, supplied by whichever component owns it. */
export type CommandHandler = (event?: KeyboardEvent) => void;

interface HandlerEntry {
  readonly handler: CommandHandler;
  readonly enabled: boolean;
}

/**
 * The callbacks, stable for the provider's whole life.
 *
 * Split from the state below, and the split is the point: `useCommandHandler`
 * and `useCommandScope` need only this half, and their callers are the heaviest
 * components in the app — `ViewerToolbar` (eleven tooltip'd buttons),
 * `DesignPointsWithDetail` (the whole DP table), the Lexical editor host. With
 * one combined context every availability flip anywhere re-rendered all three:
 * selecting a design point rendered the table a second time, and the first
 * keystroke in the report write-up re-rendered the viewer toolbar. None of them
 * reads command state at all.
 */
interface CommandsApi {
  /** Rebind a command. An empty array unbinds it; `null` restores the default. */
  readonly setBinding: (
    id: CommandId,
    bindings: readonly Binding[] | null,
  ) => void;
  /** Restore every default. */
  readonly resetAllBindings: () => void;
  /** Run a command by id. False when nothing handled it. */
  readonly runCommand: (id: CommandId) => boolean;
  readonly registerHandler: (id: CommandId, entry: HandlerEntry) => () => void;
  readonly pushScope: (scope: CommandScope) => () => void;
}

/** The state. Changes on every rebind, scope mount and availability flip. */
interface CommandsState {
  readonly keymap: Keymap;
  readonly overrides: KeymapOverrides;
  readonly resolved: readonly ResolvedBinding[];
  /** Active scopes, outermost first. Always starts with `'global'`. */
  readonly activeScopes: readonly CommandScope[];
}

const CommandsApiContext = createContext<CommandsApi | null>(null);
const CommandsStateContext = createContext<CommandsState | null>(null);

/**
 * Defaults every registration inherits. Module scope, not an inline literal:
 * `HotkeysProvider` memoizes its context on `[defaultOptions]`, so a fresh
 * object each render invalidated the VENDOR's context too — and six vendor
 * hooks read it, every mounted `useHotkeyRecorder` included. With the shortcuts
 * dialog open that was twelve extra recorder re-renders per flip.
 *
 * `conflictBehavior: 'allow'` is required, not a preference. `CommandBinder`
 * registers dead rows too, so two scopes' `Escape` bindings are ALWAYS both
 * registered with at most one enabled; the manager's conflict check runs at
 * registration and cannot see `enabled`, so 'warn' logged "'Escape' is already
 * registered. Multiple handlers will be triggered." on every boot and every
 * story — unconditionally, and falsely. This layer owns conflict reporting
 * instead: `resolveBindings` for what fires, `findKeymapConflicts` for what the
 * user is shown.
 */
const HOTKEY_DEFAULTS: HotkeysProviderOptions = {
  hotkey: { conflictBehavior: 'allow' },
  hotkeySequence: { conflictBehavior: 'allow' },
  hotkeyRecorder: { ignoreInputs: true },
};

/** Always present, always outermost. */
const ROOT_SCOPES: readonly CommandScope[] = ['global'];

export interface CommandsProviderProps {
  readonly children: ReactNode;
  /**
   * Seed overrides instead of reading localStorage. Tests and stories pass
   * this; the app does not.
   */
  readonly initialOverrides?: KeymapOverrides;
  /** Persist changes. Off in stories and tests. */
  readonly persist?: boolean;
}

export function CommandsProvider({
  children,
  initialOverrides,
  persist = true,
}: CommandsProviderProps) {
  // Read storage once, lazily — never on every render, and never at module
  // scope where a test could not control it.
  const [overrides, setOverrides] = useState<KeymapOverrides>(
    () => initialOverrides ?? loadKeymapOverrides(),
  );

  // Handler bookkeeping is split in two, and the split is the point.
  //
  // The CALLBACKS live in a ref. They are read when a key fires — never during
  // render — so a component re-registering a fresh closure every render must
  // not re-render this provider and, through it, every consumer.
  //
  // The REGISTRY (which ids are claimed, and whether each is currently
  // available) is state, because `resolveBindings` reads it during render. An
  // earlier version kept both in the ref and bumped an epoch counter to force
  // the memo to re-run; `react-hooks/refs` rejects that, and it is right to —
  // reading a ref during render means the value the memo saw and the value
  // React will paint can disagree, and the bug that produces is a shortcut
  // that works only after some unrelated re-render.
  const handlersRef = useRef(new Map<CommandId, HandlerEntry[]>());
  const [registry, setRegistry] = useState<ReadonlyMap<CommandId, boolean>>(
    () => new Map(),
  );

  // Same split for scopes. A scope is pushed by a mounting boundary, so this
  // one does drive state — the resolution depends on it.
  const [scopeStack, setScopeStack] = useState<readonly CommandScope[]>([]);

  /**
   * Push `registry` back into agreement with the top of `id`'s handler stack.
   *
   * Bails out of the state update when nothing the resolution depends on has
   * changed, so a component re-registering an identical entry on every render
   * does not re-render every consumer.
   */
  const syncRegistry = useCallback((id: CommandId) => {
    const top = handlersRef.current.get(id)?.[0];
    setRegistry((current) => {
      if (!top) {
        if (!current.has(id)) return current;
        const next = new Map(current);
        next.delete(id);
        return next;
      }
      if (current.get(id) === top.enabled) return current;
      const next = new Map(current);
      next.set(id, top.enabled);
      return next;
    });
  }, []);

  /**
   * A stack per command, not a single slot, with the INNERMOST claimant first.
   *
   * The slot version clobbered on register and DELETED on unregister, so an
   * inner component claiming an id an outer one already held would kill the
   * outer's registration permanently when the inner unmounted — the outer's
   * effect does not re-run, so the command went dead until something unrelated
   * remounted it. Reachable transiently today (a suspended route transition
   * keeps the outgoing subtree mounted while the incoming one commits) and
   * reachable by construction as soon as a second surface claims
   * `general.closeOverlay`.
   *
   * A stack makes the innermost claimant win and hands the command back on
   * unmount — the same shape as `pushScope` ten lines below, which had it right
   * all along. It also removes the identity guard the slot version needed to
   * survive a remount whose new effect ran before the old cleanup.
   *
   * The FRONT of the stack is what runs, and that is not an arbitrary end.
   * React commits passive effects bottom-up, so a descendant's
   * `useCommandHandler` effect runs BEFORE its ancestor's — which makes the
   * first entry the innermost claimant. Reading `.at(-1)` instead selected the
   * ANCESTOR, exactly inverting the rule this comment claims;
   * `CommandsProvider.test.tsx` pins the nesting case.
   *
   * Two mounted claimants is still worth a log: whichever one is not on top is
   * silently inert, which is rarely intended.
   */
  const registerHandler = useCallback(
    (id: CommandId, entry: HandlerEntry) => {
      const stack = handlersRef.current.get(id);
      if (stack && stack.length > 0) {
        logger.warn('[Commands] a second handler claimed a mounted command', {
          commandId: id,
        });
      }
      handlersRef.current.set(id, [...(stack ?? []), entry]);
      syncRegistry(id);
      return () => {
        const current = handlersRef.current.get(id);
        if (!current) return;
        const next = current.filter((held) => held !== entry);
        if (next.length === 0) handlersRef.current.delete(id);
        else handlersRef.current.set(id, next);
        syncRegistry(id);
      };
    },
    [syncRegistry],
  );

  const pushScope = useCallback((scope: CommandScope) => {
    setScopeStack((stack) => [...stack, scope]);
    let released = false;
    return () => {
      if (released) return;
      released = true;
      setScopeStack((stack) => {
        // Remove ONE occurrence, the innermost, so two boundaries of the same
        // scope nest correctly and the outer one survives the inner unmount.
        const index = stack.lastIndexOf(scope);
        if (index === -1) return stack;
        return [...stack.slice(0, index), ...stack.slice(index + 1)];
      });
    };
  }, []);

  const keymap = useMemo(() => resolveKeymap(overrides), [overrides]);

  const activeScopes = useMemo(
    () => [...ROOT_SCOPES, ...scopeStack],
    [scopeStack],
  );

  const resolved = useMemo(
    () => resolveBindings({ keymap, activeScopes, handlers: registry }),
    [keymap, activeScopes, registry],
  );

  // No dependency on `registry`, which is what keeps the API context stable:
  // `enabled` comes off the ref's top entry. Reading a ref here is fine — this
  // runs from a click, never during render.
  const runCommand = useCallback((id: CommandId) => {
    const entry = handlersRef.current.get(id)?.[0];
    if (!entry || !entry.enabled) return false;
    entry.handler();
    return true;
  }, []);

  const setBinding = useCallback(
    (id: CommandId, bindings: readonly Binding[] | null) => {
      setOverrides((current) => {
        const next: Record<string, readonly Binding[]> = { ...current };
        // `null` means "forget my customization" — distinct from `[]`, which
        // means "I want this unbound". Deleting the key is what lets the
        // default flow back through `resolveKeymap`.
        if (bindings === null) delete next[id];
        else next[id] = bindings;
        if (persist) saveKeymapOverrides(next);
        return next;
      });
    },
    [persist],
  );

  const resetAllBindings = useCallback(() => {
    setOverrides({});
    if (persist) saveKeymapOverrides({});
  }, [persist]);

  const api = useMemo<CommandsApi>(
    () => ({
      setBinding,
      resetAllBindings,
      runCommand,
      registerHandler,
      pushScope,
    }),
    [setBinding, resetAllBindings, runCommand, registerHandler, pushScope],
  );

  const state = useMemo<CommandsState>(
    () => ({ keymap, overrides, resolved, activeScopes }),
    [keymap, overrides, resolved, activeScopes],
  );

  return (
    // `HotkeysProvider` carries the defaults every registration inherits. They
    // are the floor, not the rule: `resolveBindings` merges each command's own
    // options on top, and TanStack's shape-based `ignoreInputs` inference still
    // applies where neither pins it.
    <HotkeysProvider defaultOptions={HOTKEY_DEFAULTS}>
      <CommandsApiContext.Provider value={api}>
        <CommandsStateContext.Provider value={state}>
          <CommandBinder resolved={resolved} handlersRef={handlersRef} />
          {children}
        </CommandsStateContext.Provider>
      </CommandsApiContext.Provider>
    </HotkeysProvider>
  );
}

/**
 * Registers every resolved binding, live or not.
 *
 * A separate component so the two `use*` calls re-run on the resolution rather
 * than on anything else the provider holds. It renders nothing.
 *
 * Dead rows are registered with `enabled: false` instead of being dropped:
 * TanStack updates a soft-disabled registration in place, so mounting the
 * viewer flips fifteen booleans rather than unregistering and re-registering
 * fifteen rows — and the dead ones stay visible in the Hotkeys devtools, which
 * is where "why didn't that fire" is actually answered.
 */
function CommandBinder({
  resolved,
  handlersRef,
}: {
  resolved: readonly ResolvedBinding[];
  handlersRef: React.RefObject<Map<CommandId, HandlerEntry[]>>;
}) {
  // Reading the handler through the ref at FIRE time, not at bind time, is
  // what keeps a rebind from needing a re-registration and what keeps the
  // callback out of the dependency list. The `enabled` decision is already
  // baked into the registration below, so this only has to find the callback.
  const invoke = useCallback(
    (id: CommandId) => (event: KeyboardEvent) => {
      handlersRef.current?.get(id)?.[0]?.handler(event);
    },
    [handlersRef],
  );

  const chords = useMemo<UseHotkeyDefinition[]>(
    () =>
      resolved
        .filter((row) => !isSequenceBinding(row.binding))
        .map((row) => ({
          hotkey: row.binding as UseHotkeyDefinition['hotkey'],
          callback: invoke(row.commandId),
          options: {
            enabled: row.enabled,
            ...row.options,
            meta: {
              name: commandsById.get(row.commandId)?.titleKey,
              description: row.commandId,
            },
          },
        })),
    [resolved, invoke],
  );

  const sequences = useMemo<UseHotkeySequenceDefinition[]>(
    () =>
      resolved
        .filter((row) => isSequenceBinding(row.binding))
        .map((row) => {
          const { sequenceTimeout, ...rest } = row.options;
          return {
            sequence: row.binding as UseHotkeySequenceDefinition['sequence'],
            callback: invoke(row.commandId),
            options: {
              enabled: row.enabled,
              ...rest,
              ...(sequenceTimeout === undefined
                ? {}
                : { timeout: sequenceTimeout }),
              meta: {
                name: commandsById.get(row.commandId)?.titleKey,
                description: row.commandId,
              },
            },
          };
        }),
    [resolved, invoke],
  );

  useHotkeys(chords);
  useHotkeySequences(sequences);

  return null;
}

/**
 * The command system's callbacks.
 *
 * Throws when there is no provider. A silent no-op would mean a shortcut that
 * quietly does nothing, which is the single hardest bug in this area to notice
 * — nothing is on screen to be wrong.
 *
 * Reading this and NOT `useCommandsState` is what keeps a component that merely
 * OWNS a command from re-rendering when command state changes.
 */
export function useCommandsApi(): CommandsApi {
  const context = useContext(CommandsApiContext);
  if (!context) {
    throw new Error('useCommandsApi must be used within a <CommandsProvider>');
  }
  return context;
}

/**
 * The keymap and the current resolution — for the surfaces that DISPLAY
 * shortcuts (the dialog, the recorder), never for a command's owner.
 */
export function useCommandsState(): CommandsState {
  const context = useContext(CommandsStateContext);
  if (!context) {
    throw new Error(
      'useCommandsState must be used within a <CommandsProvider>',
    );
  }
  return context;
}
